import { AlertTriangle, CheckCircle2, Circle, Info } from "lucide-react";
import { cn } from "@/lib/cn";
import type { AdminUsersOptions, RoleConfig } from "@/api/adminCenter";

/**
 * D-044 S1 — canonical role-assignment поля (MP3): ОДИН компонент для
 * wizard-кроків ②③④, drawer «Змінити роль» (#1) і майбутнього #4.
 * Лише canonical-довідник з backend (AC-ADM-010) — 0 хардкоду ролей.
 *
 * Controlled: value/onChange. sections — який зріз рендерити (wizard дає
 * по одній секції на крок; drawer — всі три разом).
 */
export interface RoleAssignmentFieldsProps {
  value: RoleConfig;
  onChange: (next: RoleConfig) => void;
  options: AdminUsersOptions;
  sections: ("tier" | "functional" | "capabilities")[];
  /** tier Адміна походить з type=admin — у drawer існуючого адміна tier locked. */
  tierLocked?: boolean;
}

export function RoleAssignmentFields({
  value,
  onChange,
  options,
  sections,
  tierLocked = false,
}: RoleAssignmentFieldsProps) {
  return (
    <div className="space-y-4">
      {sections.includes("tier") && (
        <section>
          <div className="mb-2 text-xs text-muted-foreground">
            Рівень доступу <span title="Tier">(Tier)</span> — обовʼязково
          </div>
          <div role="radiogroup" aria-label="Рівень доступу" className="space-y-2">
            {options.tiers.map((t) => {
              const selected = value.tier === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={tierLocked}
                  onClick={() =>
                    onChange({ ...value, tier: t.key, adminConfirmed: false })
                  }
                  className={cn(
                    "flex w-full items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors",
                    selected ? "border-primary bg-primary-soft" : "hover:bg-accent",
                    tierLocked && "cursor-not-allowed opacity-60",
                  )}
                >
                  {selected ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  ) : (
                    <Circle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span>
                    <span className="block text-sm">{t.label}</span>
                    <span className="block text-xs text-muted-foreground">{t.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {tierLocked && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              Рівень «Адмін» походить з типу облікового запису — зміна типу поза цим екраном.
            </p>
          )}
          {value.tier === "admin" && !tierLocked && (
            <div className="mt-2 rounded-lg bg-warning px-3 py-2.5 text-sm text-warning-foreground">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Адмін має доступ до адміністрування CRM. Призначайте лише перевіреним
                  користувачам.
                </span>
              </div>
              {/* MP6: ЯВНЕ друге підтвердження */}
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-foreground">
                <input
                  type="checkbox"
                  checked={!!value.adminConfirmed}
                  onChange={(e) => onChange({ ...value, adminConfirmed: e.target.checked })}
                  className="h-4 w-4"
                />
                <span className="text-xs">Підтверджую призначення адміністратора</span>
              </label>
            </div>
          )}
        </section>
      )}

      {sections.includes("functional") && (
        <section>
          <div className="mb-2 text-xs text-muted-foreground">Функціональна роль (опційно)</div>
          <div className="flex flex-wrap gap-1.5">
            <FnChip
              label="Без функції"
              selected={value.functional === null}
              onClick={() => onChange({ ...value, functional: null })}
            />
            {options.functional.map((f) => (
              <FnChip
                key={f.key}
                label={f.label}
                selected={value.functional === f.key}
                onClick={() => onChange({ ...value, functional: f.key })}
              />
            ))}
          </div>
          {/* MP7 */}
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            Функціональна роль визначає доступ до робочих черг і bounded context відповідного
            відділу (Finance → фінансові запити/проекція · Legal → договори/legal · Marketing →
            campaign-safe · MasterData → корекція даних).
          </p>
        </section>
      )}

      {sections.includes("capabilities") && (
        <section>
          <div className="mb-2 text-xs text-muted-foreground">
            Додаткові права (Approval capabilities)
          </div>
          <div className="space-y-2">
            {options.capabilities.map((c) => {
              const selected = value.capabilities.includes(c.key);
              return (
                <label
                  key={c.key}
                  className={cn(
                    "flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 transition-colors",
                    selected ? "border-primary bg-primary-soft" : "hover:bg-accent",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() =>
                      onChange({
                        ...value,
                        capabilities: selected
                          ? value.capabilities.filter((k) => k !== c.key)
                          : [...value.capabilities, c.key],
                      })
                    }
                    className="h-4 w-4"
                  />
                  <span className="text-sm">{c.label}</span>
                </label>
              );
            })}
          </div>
          {/* AC-ADM-034 microcopy */}
          <div className="mt-2 flex items-start gap-2 rounded-lg bg-info px-3 py-2.5 text-xs leading-relaxed text-info-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Approval capability дозволяє погоджувати договори. <b>Не</b> дає доступу до клієнтів
              і <b>не</b> є read:all.
            </span>
          </div>
        </section>
      )}
    </div>
  );
}

function FnChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "rounded-badge px-2.5 py-1 text-xs transition-colors",
        selected
          ? "bg-info text-info-foreground"
          : "bg-muted text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      {label}
    </button>
  );
}
